import type { KeyboardEvent, MouseEvent } from "react";

import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { useTrainingPlansDrawerContext } from "@Context/game/training-plans/training-plans-drawer-context";
import KeyboardKeys from "@Enums/KeyboardKeys";
import { faCheck, faPlay } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Stack, Typography, useTheme } from "@mui/material";

import {
  CompletedBadge,
  EntryCard,
  EntryContentStack,
  EntryStatusSlot,
  EntryTextStack,
  FutureSessionIndicator,
  PlayButton,
} from "@Components/training-plans/training-plans-drawer/training-plans-drawer.styled";

const TrainingPlansDrawerEntries = () => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { entries, handleEntryClick } = useTrainingPlansDrawerContext();

  if (entries.length === 0) {
    return null;
  }

  const handleEntryKeyDown = (event: KeyboardEvent<HTMLDivElement>, entry: (typeof entries)[number]) => {
    if (entry.isClickable && (event.key === KeyboardKeys.ENTER || event.key === KeyboardKeys.SPACE)) {
      event.preventDefault();
      handleEntryClick(entry);
    }
  };

  const handlePlayClick = (event: MouseEvent<HTMLDivElement>, entry: (typeof entries)[number]) => {
    event.stopPropagation();
    handleEntryClick(entry);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="textBase500" color="text.secondary">
        {i18n("training-plans.drawer.entries.title")}
      </Typography>
      <Stack spacing={1.5}>
        {entries.map(entry => {
          const entryCard = (
            <EntryCard
              key={entry.id}
              aria-label={entry.title}
              onClick={entry.isClickable ? () => handleEntryClick(entry) : undefined}
              onKeyDown={event => handleEntryKeyDown(event, entry)}
              role={entry.isClickable ? "button" : undefined}
              tabIndex={entry.isClickable ? 0 : -1}
              isClickable={entry.isClickable}
              isCompleted={entry.isCompleted}
              isCurrentSession={entry.isCurrentSession}
            >
              <EntryContentStack>
                <EntryTextStack spacing={0.5}>
                  <Typography variant="textBase600" color="dsBase.v200">
                    {entry.title}
                  </Typography>
                  <Typography variant="textS500" color="text.secondary">
                    {entry.subtitle}
                  </Typography>
                </EntryTextStack>
                <EntryStatusSlot>
                  {entry.isCompleted && (
                    <CompletedBadge>
                      <Icon icon={faCheck} color={theme.palette.dsSurface.v200} />
                    </CompletedBadge>
                  )}
                  {entry.isCurrentSession && (
                    <PlayButton onClick={event => handlePlayClick(event, entry)} aria-hidden>
                      <Icon icon={faPlay} color={theme.palette.dsBase.v200} width={8} />
                    </PlayButton>
                  )}
                  {!entry.isCompleted && !entry.isCurrentSession && <FutureSessionIndicator />}
                </EntryStatusSlot>
              </EntryContentStack>
            </EntryCard>
          );

          if (!entry.isClickable) {
            return entryCard;
          }

          return (
            <HapticsButtonWrapper key={`haptic-${entry.id}`}>
              {entryCard}
            </HapticsButtonWrapper>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default TrainingPlansDrawerEntries;
